import { AddressKey, CountryValues, VenueValues } from '../types';
import {
    COUNTRIES_REQUIRING_REGION,
    POSTAL_ERROR_BY_COUNTRY,
    POSTAL_LABELS_BY_COUNTRY,
    REGION_ERROR_BY_COUNTRY,
    REGION_LABELS_BY_COUNTRY,
} from './config';

/**
 * Determine is venue data is sufficient.
 */
export const venueHasEnoughData = (venue: VenueValues) => {
    const { name, address } = venue;
    const { country, address1, city } = address || {};

    return !!(country && name && address1 && city);
};

/**
 * Returns whether postal code is required for this country. Takes an array of
 * country objects as returned by the API like so:
 *
 * [{code: 'US', zip_required: true}, ...]
 * @param {CountryValues[]} countries List of countries
 * @param {string} countryCode Country code to test
 * @returns {boolean} Whether or not a postal code is required for the country
 */
export const isPostalRequired = (
    countries: CountryValues[],
    countryCode: string,
) => {
    const countryObj = countries.find(
        (country) => country.code === countryCode,
    );

    return !!(countryObj && countryObj['zipRequired']);
};

/**
 * Returns whether region is a required field for this country.
 * @param {string} countryCode The country code (i.e. 'US', 'CA', etc)
 * @returns {boolean} Whether or not a region is required for the country
 */
export const isRegionRequired = (countryCode: string) =>
    COUNTRIES_REQUIRING_REGION.includes(countryCode);

/**
 * Get the label for the region field for use as a form label, given a country.
 *
 * @param country
 */
export const getRegionLabel = (country: string) =>
    REGION_LABELS_BY_COUNTRY[country] || REGION_LABELS_BY_COUNTRY.DEFAULT;

/**
 * Get the label for the postal field for use as a form label, given a country.
 *
 * @param country
 */
export const getPostalLabel = (country: string) =>
    POSTAL_LABELS_BY_COUNTRY[country] || POSTAL_LABELS_BY_COUNTRY.DEFAULT;

/**
 * Get the error for the required postal field, given a country.
 *
 * @param country
 */
export const getPostalError = (country: string) =>
    POSTAL_ERROR_BY_COUNTRY[country] || POSTAL_ERROR_BY_COUNTRY.DEFAULT;

/**
 * Get the error for the required region field, given a country.
 *
 * @param country
 */
export const getRegionError = (country: string) =>
    REGION_ERROR_BY_COUNTRY[country] || REGION_ERROR_BY_COUNTRY.DEFAULT;

/**
 * Verifies if an address is valid to pass to the Google Geocoder.
 *
 * If the values listed in this function aren't provided to the geocoder,
 * the geocoder may return a random address within the US, reseting some
 * form values in the Venue form.
 *
 * @param {object} addressValues - Key/value pairs of each address piece
 * @returns {boolean} If the address is valid for the geocoder
 */
export const isAddressValidForGeocoder = (values: VenueValues) => {
    const { address: addressValues } = values;
    const valuesToValidate: AddressKey[] = ['address1', 'city', 'country'];

    for (const key of valuesToValidate) {
        if (!addressValues[key]) {
            return false;
        }
    }
    return true;
};

/**
 * Validates if venue has all required data including country-specific requirements
 */
export const isVenueComplete = (
    venue: VenueValues,
    countries: CountryValues[] = [],
): boolean => {
    // First check basic requirements
    if (!venueHasEnoughData(venue)) {
        return false;
    }

    const { address } = venue;
    const { country, region, postalCode } = address;

    // Check country-specific postal code requirement
    if (isPostalRequired(countries, country) && !postalCode) {
        return false;
    }

    // Check country-specific region requirement
    if (isRegionRequired(country) && !region) {
        return false;
    }

    return true;
};
